import { Spin } from "antd";
import { useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ISurveyBuilderSurvey } from "../../../../../@types/SurveyBuilder";
import { SelectDropdown } from "../../../../../components/SelectDropdown";
import { toPreviewSurvey } from "../../../../../utils/surveyBuilderMapping";
import { IconButton, MutedText } from "../../SurveyBuilder.styles";
import { DeviceToggle, PreviewDevice } from "./DeviceToggle";
import { PortalGraphPreview } from "./PortalGraphPreview";
import { publishSurveyPreview } from "./previewBridge";
import {
  GraphFrame,
  PhoneFrame,
  PhoneOverlay,
  PreviewIframe,
  PreviewContainer,
  PreviewControls,
} from "./PreviewTab.styles";

type PreviewMode = "patient" | "portal";

/**
 * EN-5261: the open version as the patient answers it (nothing is saved) and its portal graph
 * on sample answers. Uses the cached version, so unsaved editor changes are not shown.
 */
export function PreviewTab({ survey }: { survey: ISurveyBuilderSurvey }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PreviewMode>("patient");
  const [run, setRun] = useState(0);
  const [device, setDevice] = useState<PreviewDevice>("mobile");
  const [finished, setFinished] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);

  const previewSurvey = useMemo(() => toPreviewSurvey(survey), [survey]);
  const hasQuestions = previewSurvey.sections[0].questions.length > 0;

  // Published before the iframe's page first reads it; later publishes (a save, "Start again")
  // restart the survey inside the already loaded iframe.
  useLayoutEffect(() => {
    setFinished(false);
    publishSurveyPreview({
      survey: previewSurvey,
      onFinish: () => setFinished(true),
    });
  }, [previewSurvey, run]);

  const restart = () => setRun((r) => r + 1);

  // The answering screen lays out its slides from the window width: start again at the new size.
  const changeDevice = (next: PreviewDevice) => {
    setDevice(next);
    restart();
  };

  return (
    <PreviewContainer>
      <PreviewControls>
        <SelectDropdown
          width={240}
          value={mode}
          options={[
            {
              key: "patient",
              value: "patient",
              label: t("surveys_builder_preview_patient"),
            },
            {
              key: "portal",
              value: "portal",
              label: t("surveys_builder_preview_portal"),
            },
          ]}
          onValueChange={(value) => setMode(value as PreviewMode)}
        />
        {mode === "patient" && hasQuestions && (
          <>
            <DeviceToggle value={device} onChange={changeDevice} />
            <IconButton type="button" onClick={restart}>
              {t("surveys_builder_preview_restart")}
            </IconButton>
          </>
        )}
      </PreviewControls>

      {!hasQuestions && mode === "patient" && (
        <MutedText>{t("surveys_builder_content_noQuestions")}</MutedText>
      )}
      {/* Kept mounted while the graph is shown, so switching back doesn't reload the app in it. */}
      {hasQuestions && (
        <PhoneFrame hidden={mode !== "patient"} device={device}>
          <PreviewIframe
            device={device}
            key={survey.id}
            title={t("surveys_builder_preview_patient")}
            src="/surveybuilder/preview"
            onLoad={() => setFrameLoaded(true)}
          />
          {!frameLoaded && (
            <PhoneOverlay>
              <Spin />
            </PhoneOverlay>
          )}
          {finished && (
            <PhoneOverlay>
              <MutedText>{t("surveys_builder_preview_finished")}</MutedText>
              <IconButton type="button" onClick={restart}>
                {t("surveys_builder_preview_restart")}
              </IconButton>
            </PhoneOverlay>
          )}
        </PhoneFrame>
      )}

      {mode === "portal" && (
        <GraphFrame>
          <MutedText>{t("surveys_builder_preview_sampleData")}</MutedText>
          <PortalGraphPreview displayRules={survey.version.displayRules} />
        </GraphFrame>
      )}
    </PreviewContainer>
  );
}
